import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Building2,
  Calendar,
  Download,
  RefreshCcw,
  ChevronDown,
  TrendingUp,
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  AlertTriangle,
  CheckCircle2,
  Zap,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type DateRange = 'today' | 'yesterday' | 'week' | 'month'

interface BankAccount {
  id: string
  bankName: string
  bankCode: string
  accountNumber: string
  accountName: string
  type: 'deposit' | 'withdraw' | 'both'
  balance: number
  dailyLimit: number
  dailyUsed: number
  todayDeposit: number
  todayWithdraw: number
  txCount: number
  status: 'active' | 'inactive' | 'maintenance'
  autoTransfer: boolean
  avgProcessTime: number
  successRate: number
}

const bankColors: Record<string, string> = {
  KBANK: 'bg-green-600',
  SCB: 'bg-purple-600',
  BBL: 'bg-blue-700',
  KTB: 'bg-cyan-600',
  BAY: 'bg-yellow-500',
  TMB: 'bg-orange-500',
}

export function BanksReportPage() {
  const [dateRange, setDateRange] = useState<DateRange>('today')

  const dateRangeLabels: Record<DateRange, string> = {
    today: 'วันนี้',
    yesterday: 'เมื่อวาน',
    week: '7 วันที่ผ่านมา',
    month: 'เดือนนี้',
  }

  // Mock data
  const summaryStats = {
    totalBalance: 8450000,
    totalDeposit: 15250000,
    totalWithdraw: 12850000,
    activeAccounts: 6,
    totalAccounts: 8,
    avgSuccessRate: 98.5,
    totalTransactions: 2458,
  }

  const bankAccounts: BankAccount[] = [
    {
      id: '1',
      bankName: 'กสิกรไทย',
      bankCode: 'KBANK',
      accountNumber: '123-4-56789-0',
      accountName: 'บริษัท ทดสอบ จำกัด',
      type: 'both',
      balance: 2500000,
      dailyLimit: 3000000,
      dailyUsed: 2550000,
      todayDeposit: 5200000,
      todayWithdraw: 4100000,
      txCount: 450,
      status: 'active',
      autoTransfer: true,
      avgProcessTime: 35,
      successRate: 99.2,
    },
    {
      id: '2',
      bankName: 'ไทยพาณิชย์',
      bankCode: 'SCB',
      accountNumber: '234-5-67890-1',
      accountName: 'บริษัท ทดสอบ จำกัด',
      type: 'deposit',
      balance: 1800000,
      dailyLimit: 2500000,
      dailyUsed: 1800000,
      todayDeposit: 3800000,
      todayWithdraw: 0,
      txCount: 320,
      status: 'active',
      autoTransfer: true,
      avgProcessTime: 42,
      successRate: 98.8,
    },
    {
      id: '3',
      bankName: 'กรุงเทพ',
      bankCode: 'BBL',
      accountNumber: '345-6-78901-2',
      accountName: 'บริษัท ทดสอบ จำกัด',
      type: 'withdraw',
      balance: 1200000,
      dailyLimit: 2000000,
      dailyUsed: 900000,
      todayDeposit: 0,
      todayWithdraw: 2400000,
      txCount: 180,
      status: 'active',
      autoTransfer: false,
      avgProcessTime: 55,
      successRate: 97.5,
    },
    {
      id: '4',
      bankName: 'กรุงไทย',
      bankCode: 'KTB',
      accountNumber: '456-7-89012-3',
      accountName: 'บริษัท ทดสอบ จำกัด',
      type: 'both',
      balance: 950000,
      dailyLimit: 2500000,
      dailyUsed: 950000,
      todayDeposit: 2200000,
      todayWithdraw: 1950000,
      txCount: 220,
      status: 'maintenance',
      autoTransfer: true,
      avgProcessTime: 48,
      successRate: 98.2,
    },
    {
      id: '5',
      bankName: 'กรุงศรี',
      bankCode: 'BAY',
      accountNumber: '567-8-90123-4',
      accountName: 'บริษัท ทดสอบ จำกัด',
      type: 'deposit',
      balance: 1200000,
      dailyLimit: 2000000,
      dailyUsed: 1200000,
      todayDeposit: 2800000,
      todayWithdraw: 0,
      txCount: 185,
      status: 'active',
      autoTransfer: true,
      avgProcessTime: 38,
      successRate: 99.0,
    },
    {
      id: '6',
      bankName: 'ทหารไทยธนชาต',
      bankCode: 'TMB',
      accountNumber: '678-9-01234-5',
      accountName: 'บริษัท ทดสอบ จำกัด',
      type: 'withdraw',
      balance: 800000,
      dailyLimit: 1500000,
      dailyUsed: 800000,
      todayDeposit: 0,
      todayWithdraw: 1800000,
      txCount: 145,
      status: 'inactive',
      autoTransfer: false,
      avgProcessTime: 62,
      successRate: 96.8,
    },
  ]

  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const getStatusBadge = (status: BankAccount['status']) => {
    const config = {
      active: { label: 'ใช้งาน', className: 'bg-win/10 text-win' },
      inactive: { label: 'ปิดใช้งาน', className: 'bg-muted text-muted-foreground' },
      maintenance: { label: 'ปรับปรุง', className: 'bg-warning/10 text-warning' },
    }
    return <Badge variant="secondary" className={cn("border-0", config[status].className)}>{config[status].label}</Badge>
  }

  const getTypeBadge = (type: BankAccount['type']) => {
    const config = {
      deposit: { label: 'รับฝาก', className: 'bg-win/10 text-win' },
      withdraw: { label: 'ถอน', className: 'bg-orange-500/10 text-orange-500' },
      both: { label: 'ฝาก/ถอน', className: 'bg-primary/10 text-primary' },
    }
    return <Badge variant="secondary" className={cn("border-0", config[type].className)}>{config[type].label}</Badge>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">รายงานบัญชีธนาคาร</h1>
          <p className="text-muted-foreground">ประสิทธิภาพและสถานะบัญชีธนาคาร</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Calendar className="h-4 w-4" />
                {dateRangeLabels[dateRange]}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(dateRangeLabels).map(([key, label]) => (
                <DropdownMenuItem key={key} onClick={() => setDateRange(key as DateRange)}>
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="icon">
            <RefreshCcw className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            ส่งออก
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-primary/10">
                <Wallet className="h-6 w-6 text-primary" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">ยอดเงินคงเหลือรวม</p>
              <p className="text-2xl font-bold">{formatCurrency(summaryStats.totalBalance)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-win/10">
                <ArrowDownToLine className="h-6 w-6 text-win" />
              </div>
              <Badge variant="secondary" className="bg-win/10 text-win border-0 gap-1">
                <TrendingUp className="h-3 w-3" />
                +12%
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">ยอดฝากวันนี้</p>
              <p className="text-2xl font-bold text-win">{formatCurrency(summaryStats.totalDeposit)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-orange-500/10">
                <ArrowUpFromLine className="h-6 w-6 text-orange-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">ยอดถอนวันนี้</p>
              <p className="text-2xl font-bold">{formatCurrency(summaryStats.totalWithdraw)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-blue-500/10">
                <Building2 className="h-6 w-6 text-blue-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">บัญชีใช้งาน</p>
              <p className="text-2xl font-bold">
                {summaryStats.activeAccounts}/{summaryStats.totalAccounts}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bank Accounts Detail */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            รายละเอียดบัญชีธนาคาร
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-y">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">ธนาคาร</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">ประเภท</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">ยอดคงเหลือ</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">ฝากวันนี้</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">ถอนวันนี้</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">วงเงิน</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">ประสิทธิภาพ</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">สถานะ</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">Auto</th>
                </tr>
              </thead>
              <tbody>
                {bankAccounts.map((bank) => {
                  const utilizationPercent = Math.round((bank.dailyUsed / bank.dailyLimit) * 100)
                  return (
                    <tr key={bank.id} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className={cn("w-3 h-10 rounded-full", bankColors[bank.bankCode])} />
                          <div>
                            <p className="font-medium">{bank.bankName}</p>
                            <p className="text-xs text-muted-foreground">{bank.accountNumber}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {getTypeBadge(bank.type)}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="font-semibold">{formatCurrency(bank.balance)}</span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-win">{formatCurrency(bank.todayDeposit)}</span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-orange-500">{formatCurrency(bank.todayWithdraw)}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="w-32 mx-auto">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-muted-foreground">{utilizationPercent}%</span>
                          </div>
                          <Progress 
                            value={utilizationPercent} 
                            className="h-2"
                            indicatorClassName={cn(
                              utilizationPercent >= 90 && "bg-destructive",
                              utilizationPercent >= 70 && utilizationPercent < 90 && "bg-warning",
                              utilizationPercent < 70 && "bg-win"
                            )}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-center">
                          <p className="text-sm font-medium">{bank.successRate}%</p>
                          <p className="text-xs text-muted-foreground">{bank.avgProcessTime}s avg</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {getStatusBadge(bank.status)}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Switch checked={bank.autoTransfer} disabled />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Performance Insights */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="h-5 w-5 text-win" />
              บัญชีประสิทธิภาพสูง
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {bankAccounts
                .filter(b => b.successRate >= 99)
                .map((bank) => (
                  <div key={bank.id} className="flex items-center justify-between p-2 rounded-lg bg-win/5">
                    <div className="flex items-center gap-2">
                      <div className={cn("w-2 h-6 rounded-full", bankColors[bank.bankCode])} />
                      <span className="text-sm font-medium">{bank.bankName}</span>
                    </div>
                    <span className="text-sm text-win font-bold">{bank.successRate}%</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-warning" />
              บัญชีใกล้เต็มวงเงิน
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {bankAccounts
                .filter(b => (b.dailyUsed / b.dailyLimit) >= 0.8)
                .map((bank) => {
                  const percent = Math.round((bank.dailyUsed / bank.dailyLimit) * 100)
                  return (
                    <div key={bank.id} className="flex items-center justify-between p-2 rounded-lg bg-warning/5">
                      <div className="flex items-center gap-2">
                        <div className={cn("w-2 h-6 rounded-full", bankColors[bank.bankCode])} />
                        <span className="text-sm font-medium">{bank.bankName}</span>
                      </div>
                      <span className="text-sm text-warning font-bold">{percent}%</span>
                    </div>
                  )
                })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="h-5 w-5 text-blue-500" />
              ความเร็วเฉลี่ย
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {bankAccounts
                .sort((a, b) => a.avgProcessTime - b.avgProcessTime)
                .slice(0, 4)
                .map((bank) => (
                  <div key={bank.id} className="flex items-center justify-between p-2 rounded-lg bg-blue-500/5">
                    <div className="flex items-center gap-2">
                      <div className={cn("w-2 h-6 rounded-full", bankColors[bank.bankCode])} />
                      <span className="text-sm font-medium">{bank.bankName}</span>
                    </div>
                    <span className="text-sm text-blue-500 font-bold">{bank.avgProcessTime}s</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}








